/**
 * Where an online order is, between being paid for and leaving the shop
 * (docs/STOREFRONT.md S5, decision 156).
 *
 * One small state machine, and the only thing the API consults before it
 * moves an order: the stages staff see in *Online orders*, which moves are
 * allowed from each, which of them completes the sale, and which the customer
 * is emailed about. Pure, so it is tested here rather than through a screen.
 */

import type { OrderFulfillment } from './order-details';

export type OrderStage = 'new' | 'picking' | 'ready' | 'shipped' | 'collected' | 'canceled';

export const ORDER_STAGES: readonly OrderStage[] = [
  'new',
  'picking',
  'ready',
  'shipped',
  'collected',
  'canceled',
];

/** What staff read, on the order and on the button that moves it there. */
export const ORDER_STAGE_WORDS: Readonly<
  Record<OrderStage, { readonly label: string; readonly action: string }>
> = {
  new: { label: 'New', action: 'New' },
  picking: { label: 'Picking', action: 'Start picking' },
  ready: { label: 'Ready for pickup', action: 'Ready for pickup' },
  shipped: { label: 'Shipped', action: 'Shipped' },
  collected: { label: 'Collected', action: 'Collected' },
  canceled: { label: 'Canceled', action: 'Cancel order' },
};

/**
 * Where an order may go next.
 *
 * Forward only, one stage at a time. A pickup is made ready and collected; a
 * delivery is shipped straight from picking, because shipping *is* it
 * leaving. Canceling is possible until the goods have left — after that it
 * is a return, which the register already does.
 */
export function nextStages(stage: OrderStage, fulfillment: OrderFulfillment): OrderStage[] {
  switch (stage) {
    case 'new':
      return ['picking', 'canceled'];
    case 'picking':
      return fulfillment === 'ship' ? ['shipped', 'canceled'] : ['ready', 'canceled'];
    case 'ready':
      return fulfillment === 'pickup' ? ['collected', 'canceled'] : [];
    case 'shipped':
    case 'collected':
    case 'canceled':
      return [];
  }
}

export function canMove(from: OrderStage, to: OrderStage, fulfillment: OrderFulfillment): boolean {
  return nextStages(from, fulfillment).includes(to);
}

/** The goods leave the shop here, so the sale completes here (decision 155). */
export function completesSale(stage: OrderStage): boolean {
  return stage === 'collected' || stage === 'shipped';
}

/**
 * The work waiting on staff — what the tab counts.
 *
 * *Ready* is not in it: an order ready for pickup is waiting on the customer,
 * and a count that included it would never reach zero on a quiet day.
 */
export function needsDoing(stage: OrderStage): boolean {
  return stage === 'new' || stage === 'picking';
}

/** The moves the customer hears about. Picking is the shop's business. */
export function emailsCustomer(stage: OrderStage): boolean {
  return stage === 'ready' || stage === 'shipped' || stage === 'canceled';
}
